import { Component } from '@theme/component';

/**
 * Manages the mobile menu drawer's language/currency picker.
 *
 * Lets the shopper preview a language and/or currency choice locally
 * (highlighting the tapped option) without submitting anything, then
 * either commits the choice via the standard `/localization` form on
 * "Save", or discards it and returns to the main menu on "Cancel".
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} [form] - The localization form.
 * @property {HTMLInputElement} [languageInput] - Hidden input holding the pending language code.
 * @property {HTMLInputElement} [currencyInput] - Hidden input holding the pending currency code.
 * @property {HTMLUListElement} [languageOptions] - The list of selectable language options.
 * @property {HTMLUListElement} [currencyOptions] - The list of selectable currency options.
 * @property {HTMLElement} [summaryLabel] - The label shown on the collapsed row (e.g. "EN / $").
 *
 * @extends {Component<Refs>}
 */
class MobileLocalizationPicker extends Component {
  /**
   * Select a language locally (does not submit the form).
   * @param {string} code
   */
  selectLanguage(code) {
    this.#markSelected(this.refs.languageOptions, code);

    if (this.refs.languageInput) {
      this.refs.languageInput.value = code;
    }

    this.#updateSummary();
  }

  /**
   * Select a currency locally (does not submit the form).
   * @param {string} code
   */
  selectCurrency(code) {
    this.#markSelected(this.refs.currencyOptions, code);

    if (this.refs.currencyInput) {
      this.refs.currencyInput.value = code;
    }

    this.#updateSummary();
  }

  /**
   * Commit the pending language/currency selection.
   */
  save() {
    if (!this.refs.form) return;

    if (typeof this.refs.form.requestSubmit === 'function') {
      this.refs.form.requestSubmit();
    } else {
      this.refs.form.submit();
    }
  }

  /**
   * Discard the pending selection and go back to the main menu.
   * @param {Event} event
   */
  cancel(event) {
    this.#reset();

    const headerDrawer = this.closest('header-drawer');
    if (headerDrawer && typeof (/** @type {any} */ (headerDrawer).back) === 'function') {
      /** @type {any} */ (headerDrawer).back(event);
    }
  }

  /**
   * @param {HTMLElement | undefined} list
   * @param {string} code
   */
  #markSelected(list, code) {
    if (!list) return;

    list.querySelectorAll('.menu-drawer__lang-currency-option').forEach((button) => {
      button.classList.toggle('is-current', button.getAttribute('data-code') === code);
    });
  }

  #reset() {
    [this.refs.languageOptions, this.refs.currencyOptions].forEach((list) => {
      if (!list) return;

      const current = list.getAttribute('data-current');
      this.#markSelected(list, current ?? '');
    });

    if (this.refs.languageInput && this.refs.languageOptions) {
      this.refs.languageInput.value = this.refs.languageOptions.getAttribute('data-current') ?? this.refs.languageInput.value;
    }

    if (this.refs.currencyInput && this.refs.currencyOptions) {
      this.refs.currencyInput.value = this.refs.currencyOptions.getAttribute('data-current') ?? this.refs.currencyInput.value;
    }

    this.#updateSummary();
  }

  #updateSummary() {
    const label = this.refs.summaryLabel;
    if (!label) return;

    const languageCode = this.refs.languageOptions ? this.refs.languageInput?.value : undefined;
    const currencyCode = this.refs.currencyOptions ? this.refs.currencyInput?.value : undefined;

    // Update text inside dedicated child spans instead of replacing
    // `label.textContent` wholesale — that used to destroy
    // <span class="current-currency-placeholder">, which the BUCKS
    // currency script (sections/header.liquid) also writes into.
    const languageEl = label.querySelector('.current-language-placeholder');
    if (languageEl && languageCode) {
      languageEl.textContent = languageCode.toUpperCase();
    }

    if (currencyCode) {
      const activeButton = this.refs.currencyOptions?.querySelector(`[data-code="${CSS.escape(currencyCode)}"]`);
      const symbol = activeButton?.getAttribute('data-symbol');
      const currencyEl = label.querySelector('.current-currency-placeholder');
      if (currencyEl) {
        currencyEl.textContent = symbol || currencyCode;
      }
    }
  }
}

if (!customElements.get('mobile-localization-picker-component')) {
  customElements.define('mobile-localization-picker-component', MobileLocalizationPicker);
}